import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Search } from 'lucide-react';
import {
  DEALER_STOCK_OPTIONS,
  HOME_VALUES,
  NO_CURRENT_VALUE,
  PROCESSED_REASON_CODES,
  REASON_LABELS,
  VENDOR_STOCK_OPTIONS,
  fetchHomeValueChecksProcessed,
  fetchHomeValueVendors,
} from '../api/home_value_checks';
import type {
  DealerStockFilter,
  HomeValueCheck,
  HomeValueProcessedFilters,
  HomeValueReasonCode,
  VendorOption,
  VendorStockFilter,
} from '../api/home_value_checks';
import { ReviewStatusBadge } from '../components/badges';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
} from '../components/data-table/columns';
import {
  LocationChips,
  nsIdColumn,
  observedColumn,
  dealerColumn,
  reasonColumn,
  reviewerColumn,
  skuColumn,
  syncedColumn,
  transitionColumn,
  vendorColumn,
  vendorTotalColumn,
} from '../components/data-table/homeValueColumns';
import {
  RowActionMenu,
  RowActions,
  type RowMenuItem,
} from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDateTime, fmtVendor } from '../utils/format';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const HOME_VALUE_OPTIONS = [
  { value: '', label: 'All' },
  ...HOME_VALUES.map(v => ({ value: v, label: v })),
];

const CURRENT_VALUE_OPTIONS = [
  ...HOME_VALUE_OPTIONS,
  { value: NO_CURRENT_VALUE, label: 'No value set' },
];

/**
 * Outcomes, in the order an operator asks about them. Approved / auto-approved
 * rows appear here only once their push has landed — until then they are in
 * Queue (the price lane's contract).
 *
 * `cancelled` is on the list deliberately: "we proposed X, then NetSuite already
 * had X" and "a newer scrape replaced this proposal" are both real answers, and
 * hiding them would make the lane look like it loses work.
 */
const OUTCOME_OPTIONS = [
  { value: '',              label: 'All' },
  { value: 'approved',      label: 'Approved · pushed',      dot: 'bg-green-500' },
  { value: 'auto_approved', label: 'Auto-approved · pushed', dot: 'bg-green-500' },
  { value: 'rejected',      label: 'Rejected',               dot: 'bg-amber-500' },
  { value: 'cancelled',     label: 'Cancelled',              dot: 'bg-zinc-400'  },
];

// Derived from the shared list, not hand-copied: a reason code added to
// `rules_config.py` reaches this filter the moment it reaches REASON_LABELS.
const ALL_REASON_OPTIONS: HomeValueReasonCode[] = PROCESSED_REASON_CODES;

/** Home Value → Processed: the terminal log for the availability lane — pushed,
 *  rejected or cancelled. Read-only; anything still actionable lives in Queue. */
export default function HomeValueProcessedPage() {
  const [filters, setFilters] = useState<HomeValueProcessedFilters>({ page: 1, per_page: 50 });
  const [vendorCodes, setVendorCodes] = useState<string[]>([]);
  const [reasonCodes, setReasonCodes] = useState<string[]>([]);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [search, setSearch] = useState('');

  const [items, setItems]     = useState<HomeValueCheck[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchHomeValueChecksProcessed({
        ...filters,
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        reason_codes: reasonCodes.length ? (reasonCodes as HomeValueReasonCode[]) : undefined,
        q: search.trim() || undefined,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [filters, vendorCodes, reasonCodes, search]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchHomeValueVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  function resetFilters() {
    setVendorCodes([]);
    setReasonCodes([]);
    setSearch('');
    setFilters({ page: 1, per_page: filters.per_page });
  }

  const hasActiveFilters =
    vendorCodes.length > 0 ||
    reasonCodes.length > 0 ||
    !!search ||
    !!filters.review_status ||
    !!filters.proposed_home_value ||
    !!filters.current_home_value ||
    !!filters.vendor_stock || !!filters.dealer_stock ||
    !!filters.synced_from || !!filters.synced_to;

  const columns = useMemo<ColumnDef<HomeValueCheck>[]>(() => [
    expandColumn<HomeValueCheck>(),
    skuColumn,
    vendorColumn,
    nsIdColumn,
    transitionColumn,
    reasonColumn,
    vendorTotalColumn,
    dealerColumn,
    {
      id: 'outcome',
      meta: { label: 'Outcome' },
      header: 'Outcome',
      cell: ({ row }) => <ReviewStatusBadge status={row.original.review_status} />,
    },
    observedColumn,
    reviewerColumn,
    syncedColumn,
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [
          {
            label: 'Details',
            icon: Search,
            href: `/all-items/${item.ns_item_id}?from=home-value&checkId=${item.id}`,
          },
          {
            label: 'Open in NetSuite',
            icon: ExternalLink,
            href: `${NS_BASE_URL}${item.ns_item_id}`,
          },
        ];
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento',
            icon: ExternalLink,
            href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot',
            icon: Camera,
            href: item.screenshot_url,
            separatorBefore: true,
          });
        }
        // Read-only by design: undo lives on the Queue tab, where a manual
        // approval can still be pulled back before its push lands.
        return (
          <RowActions>
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  const perPage    = filters.per_page ?? 50;
  const page       = filters.page ?? 1;
  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <PageHeading title="Home Value — Processed" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
          />
          <FilterSelect
            label="Outcome"
            options={OUTCOME_OPTIONS}
            value={filters.review_status ?? ''}
            onChange={v =>
              setFilters(f => ({
                ...f,
                review_status: (v || undefined) as HomeValueProcessedFilters['review_status'],
                page: 1,
              }))
            }
          />
          <FilterSelect
            label="From"
            options={CURRENT_VALUE_OPTIONS}
            value={filters.current_home_value ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, current_home_value: v || undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="To"
            options={HOME_VALUE_OPTIONS}
            value={filters.proposed_home_value ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, proposed_home_value: v || undefined, page: 1 }))
            }
          />
          <MultiSelect
            label="Reason"
            options={ALL_REASON_OPTIONS.map(rc => ({ value: rc, label: REASON_LABELS[rc] }))}
            selected={reasonCodes}
            onChange={next => { setReasonCodes(next); setFilters(f => ({ ...f, page: 1 })); }}
            searchPlaceholder="Search reasons…"
          />
          <FilterSelect
            label="Vendor stock"
            options={VENDOR_STOCK_OPTIONS}
            value={filters.vendor_stock ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, vendor_stock: (v || undefined) as VendorStockFilter | undefined, page: 1 }))
            }
          />
          <FilterSelect
            label="Dealer stock"
            options={DEALER_STOCK_OPTIONS}
            value={filters.dealer_stock ?? ''}
            onChange={v =>
              setFilters(f => ({ ...f, dealer_stock: (v || undefined) as DealerStockFilter | undefined, page: 1 }))
            }
          />
        </FilterRow>

        <FilterRow className="gap-4">
          <FilterField label="Search">
            <Input
              placeholder="SKU or name"
              className="w-[200px]"
              value={search}
              onChange={e => { setSearch(e.target.value); setFilters(f => ({ ...f, page: 1 })); }}
            />
          </FilterField>

          <FilterField label="Synced">
            <Input
              type="date"
              className="w-[140px]"
              value={filters.synced_from ?? ''}
              onChange={e =>
                setFilters(f => ({ ...f, synced_from: e.target.value || undefined, page: 1 }))
              }
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={filters.synced_to ?? ''}
              onChange={e =>
                setFilters(f => ({ ...f, synced_to: e.target.value || undefined, page: 1 }))
              }
            />
          </FilterField>

          <div className="ml-auto flex items-center gap-2">
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="Nothing processed yet"
        renderExpanded={row => {
          const item = row.original;
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    ['Description', item.item_name ?? '—'],
                    ['Manufacturer', item.manufacturer ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{item.sku ?? '—'}</span>],
                    // After a successful push + ns_sync tick this equals the
                    // proposed value — the confirmation that NetSuite took it.
                    ['NetSuite Home Value now', item.ns_home_value_now ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor stock at decision time">
                <LocationChips locations={item.locations} />
                <ExpandedFields
                  rows={[
                    ['PDC total', item.pdc_total ?? '—'],
                    ['Dealer stock', item.dealer_total ?? '—'],
                    ['Observed', fmtDateTime(item.availability_observed_at)],
                    ['Reviewed', fmtDateTime(item.reviewed_at)],
                    ['Synced', fmtDateTime(item.ns_pushed_at)],
                    ['Push error', item.ns_push_error ?? '—'],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={item.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={p => setFilters(f => ({ ...f, page: p }))}
        onPerPageChange={n => setFilters(f => ({ ...f, per_page: n, page: 1 }))}
      />
    </>
  );
}
